import { useState, useEffect } from "react";
import { useController } from "react-hook-form";

const useHierarchicalPermissions = (control, name, defaultValue) => {
  const [permissionsState, setPermissionsState] = useState({});

  const {
    field: { onChange, value, ref },
    fieldState: { error },
  } = useController({
    name,
    control,
    defaultValue: defaultValue ?? [],
  });

  // Initialize permissions state from grouped permissions
  useEffect(() => {
    if (value && value.length > 0) {
      const newState = {};
      value.forEach(id => {
        newState[id] = true;
      });
      setPermissionsState(newState);
    }
  }, []);

  const handleParentChange = (e, parent, children) => {
    const isChecked = e.target.checked;
    const childIds = children.map(child => child.value);
    
    let newValue;
    if (isChecked) {
      // Add all children
      const uniqueValues = new Set([...value, ...childIds]);
      newValue = Array.from(uniqueValues);
    } else {
      // Remove all children
      newValue = value.filter(id => !childIds.includes(id));
    }
    
    onChange(newValue);
  };

  const handleChildChange = (e, childId, parentId, siblings) => {
    const isChecked = e.target.checked;
    
    let newValue;
    if (isChecked) {
      // Add the child
      newValue = [...value, childId];
    } else {
      // Remove the child
      newValue = value.filter(id => id !== childId);
    }
    
    onChange(newValue);
  };

  const getParentState = (children) => {
    const childIds = children.map(child => child.value);
    const checkedChildren = childIds.filter(id => value.includes(id));
    
    if (checkedChildren.length === 0) {
      return { checked: false, indeterminate: false };
    } else if (checkedChildren.length === childIds.length) {
      return { checked: true, indeterminate: false };
    } else {
      return { checked: false, indeterminate: true };
    }
  };

  const isChildChecked = (childId) => {
    return value.includes(childId);
  };

  return {
    value,
    ref,
    error,
    handleParentChange,
    handleChildChange,
    getParentState,
    isChildChecked,
  };
};

export default useHierarchicalPermissions;
